// TODO: correct naming conventions (within FE-specific code)
import React, { Fragment, useContext, useState } from 'react';
import { Dialog, DialogActions, DialogContent } from '@material-ui/core';
import Delete from '@material-ui/icons/Delete';
import { Button, IconButton, LoadingBounce, Tooltip, usePhoneView } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { ModalTitle } from 'components/ClosableDialog/ModalTitle';
import QtySelector from 'components/QtySelector/qtySelector';
import { OrderDetailContext } from 'compositions/OrderDetail/OrderDetailContext';
import { InstallationDetail } from 'compositions/Wizard/SelectTiresAndParts/MountingOption/InstallationDetails';
import { MountingOptionsSelector } from 'compositions/Wizard/SelectTiresAndParts/MountingOption/MountingOptionsSelector';
import { getDefaultOptions } from 'compositions/Wizard/SelectTiresAndParts/MountingOption/defaultOptionsSelection';
import {
  getGroupedOptions,
  getGroupedTiresFromOrder,
} from 'compositions/Wizard/SelectTiresAndParts/MountingOption/getGroupedTires';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { useGetAdditionalServices } from 'hooks/useGetAdditionalServices';
import { useGetPrices } from 'hooks/useGetPrices';
import hash from 'object-hash';
import { OrderType } from 'types/OrderType';
import { UFOPart } from 'types/UFOTypes';
import { INSTALLATION_TPMS, PQL_MOUNTING_WHEEL } from 'utils/constantValues';
import { getMspn } from 'utils/getMspn';
import { CommonPricesParamsType } from 'utils/getPricesCallCommonParams';
import { getUniqueHash } from 'utils/getUniqueHash';
import { isInstallation } from './isInstallation';

interface MountingOptionsModalProps {
  order: OrderType;
  open: boolean;
  onClose: () => void;
  maxValue: number;
  defaultValue: number;
  setParts: (parts: any[]) => void;
  commonPricesParams: CommonPricesParamsType;
}

export type SelectedMountingOptionType = {
  installation: boolean | null;
  wheels: boolean;
  tpms: boolean;
  added: boolean;
  qty: number;
  related_to?: string;
};

export function MountingOptionsModalOrderDetail(props: MountingOptionsModalProps) {
  const { t } = useTranslation();
  const mobileView = usePhoneView();
  const isDealer = useCurrentLocationIsDealer();
  const { mountingOptionsModalOpen, getPql: getPqlContext, setPql: setPqlContext } = useContext(OrderDetailContext);
  const [reqPqlForm, setReqPqlForm] = useState(false);

  const { open, onClose, maxValue, defaultValue, setParts, order, commonPricesParams } = props;

  const getPql = (mspn: string, relatedTireMspn: string) =>
    getPqlContext(mspn, relatedTireMspn) ||
    order.parts.find((part) => {
      const relatedTire = order.parts.find((p) => p.related_to === part.related_to && p.subtype === 'TIRE');
      if (getMspn(part) === mspn && relatedTireMspn === relatedTire?.mspn) return true;
      return false;
    })?.invoice_price ||
    0;

  const [openOption, setOpenOption] = useState<number | null>(null);
  const tire = mountingOptionsModalOpen || ({} as UFOPart);

  const groupedParts = getGroupedTiresFromOrder(order.parts);
  const stockingGroupedParts = getGroupedOptions(groupedParts).find((group) => group.mspn === tire.mspn)?.options;
  const [selectedMountingOptions, setSelectedMountingOptions] = useState<SelectedMountingOptionType[]>(
    stockingGroupedParts || getDefaultOptions(defaultValue, true),
  );

  const wheelMspn = PQL_MOUNTING_WHEEL;
  const tpmsMspn = INSTALLATION_TPMS;

  const additionalServicesResp = useGetAdditionalServices({
    with_service_mspn: true,
    fleet_id: commonPricesParams.fleet_id,
    dealer_id: commonPricesParams.provider_id,
    isMobile: order.additional_information.appointment_type === "is_mobile",
    query: PQL_MOUNTING_WHEEL,
  });
  const wheelIsPql: boolean =
    additionalServicesResp?.data?.addlServices.find((service) => service.mspn === wheelMspn)?.ispql || false;
  const pricesResponse = useGetPrices({
    ...commonPricesParams,
    parts: [
      ...tire.service_mspn.map((service) => ({ mspn: service, qty: 1, is_pql: false })),
      { mspn: wheelMspn, qty: 1, is_pql: wheelIsPql },
    ],
  });

  const tpmsPrice = pricesResponse?.data?.parts?.find((price) => getMspn(price) === tpmsMspn);
  const wheelsPrice =
    pricesResponse?.data?.pqlParts?.find((price) => getMspn(price) === wheelMspn) ||
    pricesResponse?.data?.parts?.find((price) => getMspn(price) === wheelMspn);
  const installation = pricesResponse?.data?.parts?.find(
    (price) => price.part_number && tire.service_mspn.includes(price.part_number),
  );

  let editedInstallations: any[] = order.parts.filter((part) => part.subtype !== 'TIRE');
  let editedTires: any[] = order.parts.filter((part) => part.subtype === 'TIRE');

  const updateParts = () => {
    selectedMountingOptions.forEach((selectedOption) => {
      const optionExist = !!selectedOption.related_to;
      const uniqHash = selectedOption.related_to || getUniqueHash();
      const pqlPrice = Number(getPql(PQL_MOUNTING_WHEEL, getMspn(tire) as string));

      if (optionExist) {
        const installationAdded = selectedOption.installation;
        const tpmsAdded = selectedOption.installation && selectedOption.tpms;
        const wheelsAdded = selectedOption.installation && selectedOption.wheels;
        editedInstallations = editedInstallations.map((i) => {
          let installationType = '';
          // TODO: fix typing
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
          if (isInstallation(i.mspn)) installationType = 'installation';
          if (i.mspn === INSTALLATION_TPMS) installationType = 'tpms';
          if (i.mspn === PQL_MOUNTING_WHEEL) installationType = 'wheels';

          const added =
            (installationAdded && installationType === 'installation') ||
            (tpmsAdded && installationType === 'tpms') ||
            (wheelsAdded && installationType === 'wheels');

          if (i.related_to === selectedOption.related_to) {
            return {
              ...i,
              quantity: selectedOption.qty,
              removed: !added,
              deleted: !selectedOption.added,
              invoice_price: i.pql_pricing ? pqlPrice : undefined,
              final_invoice_price: i.pql_pricing ? pqlPrice * selectedOption.qty : undefined,
            };
          }
          return i;
        });
        editedTires = editedTires.map((st) => {
          if (st.related_to === selectedOption.related_to) {
            return {
              ...st,
              quantity: selectedOption.qty,
              removed: !selectedOption.added,
              deleted: !selectedOption.added,
              related_to: uniqHash,
            };
          }
          return st;
        });
      } else if (selectedOption.added) {
        if (selectedOption.installation) {
          editedInstallations.push({
            related_to: uniqHash,
            quantity: selectedOption.qty,
            mspn: getMspn(installation),
          });
        }
        if (selectedOption.installation && selectedOption.tpms) {
          editedInstallations.push({
            related_to: uniqHash,
            quantity: selectedOption.qty,
            mspn: getMspn(tpmsPrice),
          });
        }
        if (selectedOption.installation && selectedOption.wheels) {
          editedInstallations.push({
            related_to: uniqHash,
            quantity: selectedOption.qty,
            mspn: wheelMspn,
            invoice_price: pqlPrice || undefined,
            final_invoice_price: pqlPrice ? pqlPrice * selectedOption.qty : undefined,
            manual_pricing: !!pqlPrice,
            pql_pricing: !!pqlPrice,
          });
        }
        editedTires.push({
          ufo_details: [tire],
          subtype: 'TIRE',
          mspn: getMspn(tire),
          quantity: selectedOption.qty,
          related_to: uniqHash,
          id: undefined,
        });
      }
    });
    setParts([...editedTires, ...editedInstallations]);
    onClose();
  };

  if (pricesResponse.isLoading || additionalServicesResp.isLoading) return <LoadingBounce />;

  const firstNotAddedCombo = selectedMountingOptions.findIndex((option) => !option.added);
  const tabsAdded = selectedMountingOptions.filter((option) => option.added).length;
  const missingPrice =
    wheelIsPql &&
    !isDealer &&
    stockingGroupedParts?.some((o) => o.wheels && o.added && !getPql(PQL_MOUNTING_WHEEL, getMspn(tire) as string));

  const othersTiresCount = order.parts?.filter((p) => p.subtype === 'TIRE' && getMspn(p) !== getMspn(tire)).reduce((count, tire) => (!tire.removed ? count + (tire.quantity || 0) : count), 0) || 0;
  const currentTireCount = selectedMountingOptions.filter((option) => option.added).reduce((acc, curr) => acc + curr.qty, 0);
  const tiresCount = othersTiresCount + currentTireCount;

  return (
    <>
      <Dialog onClose={onClose} aria-labelledby="customized-dialog-title" open={open} fullWidth maxWidth="sm" fullScreen={mobileView}>
        <ModalTitle title={t('Mounting Options')} onClose={onClose} />
        <DialogContent dividers style={{ height: 550 }}>
          {selectedMountingOptions.map((option, index) => (
            <Fragment key={hash(option)}>
              {option.added && openOption === null && (
                <InstallationDetail
                  reqPqlForm={reqPqlForm}
                  setReqPqlForm={setReqPqlForm}
                  relatedTireMspn={tire.mspn}
                  option={option}
                  onOpen={openOption === index ? () => setOpenOption(null) : () => setOpenOption(index)}
                  comboNumber={index + 1}
                  prices={{ installation, tpms: tpmsPrice, wheels: wheelsPrice }}
                  setOrderWheelPQL={(mspn, price) => {
                    editedInstallations = editedInstallations.map((ei) => {
                      if (ei.related_to === option.related_to && getMspn(ei) === mspn) {
                        return {
                          ...ei,
                          invoice_price: price,
                          final_invoice_price: price * option.qty,
                          manual_pricing: true,
                        };
                      }
                      return ei;
                    });
                    setPqlContext(mspn, price, tire.mspn);
                  }}
                  orderWheelPQL={
                    getPql(PQL_MOUNTING_WHEEL, getMspn(tire) as string)
                      ? Number(getPql(PQL_MOUNTING_WHEEL, getMspn(tire) as string))
                      : undefined
                  }
                  qtySelectorComponent={
                    <QtySelector
                      value={option.qty}
                      maxValue={maxValue}
                      defaultValue={defaultValue}
                      onChange={(v) => {
                        const editedSelectedMountingOptions = [...selectedMountingOptions];
                        editedSelectedMountingOptions[index].qty = v;
                        setSelectedMountingOptions(editedSelectedMountingOptions);
                      }}
                      selected
                      tiresCount={tiresCount}
                    />
                  }
                  deleteOptionComponent={
                    <IconButton
                      disabled={tabsAdded === 1}
                      color="primary"
                      onClick={() => {
                        const editedSelectedMountingOptions = [...selectedMountingOptions];
                        editedSelectedMountingOptions[index].added = false;
                        setSelectedMountingOptions(editedSelectedMountingOptions);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  }
                />
              )}
              {openOption === index && (
                <MountingOptionsSelector
                  qtyToAdd={option.qty}
                  selectedOptions={selectedMountingOptions}
                  setSelectedOptions={setSelectedMountingOptions}
                  optionNumber={index}
                  prices={{ installation, tpms: tpmsPrice, wheels: wheelsPrice }}
                />
              )}
            </Fragment>
          ))}
        </DialogContent>
        <DialogActions>
          {openOption === null && tabsAdded < 5 && (
            <Button
              onClick={() => {
                const editedSelectedMountingOptions = [...selectedMountingOptions];
                editedSelectedMountingOptions[firstNotAddedCombo].added = true;
                setSelectedMountingOptions(editedSelectedMountingOptions);
                setOpenOption(firstNotAddedCombo);
              }}
              color="primary"
              variant="contained"
              size="small"
            >
              {t('+ Add new configuration')}
            </Button>
          )}
          {openOption === null && (
            <Tooltip title={missingPrice ? `${t('Please provide the new Rims/Wheels price')}` : ''}>
              <div>
                <Button onClick={updateParts} color="primary" variant="contained" size="small" disabled={missingPrice}>
                  {t('Save configurations')}
                </Button>
              </div>
            </Tooltip>
          )}
          {openOption !== null && (
            <Button onClick={() => setOpenOption(null)} color="primary" variant="contained" size="small">
              {t('Confirm Configuration')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
